import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import type { Video, VideoListRequest } from "@/model/videos";
import { fetchVideoList } from "@/services/videos-service";
import type { RootState } from "@/store/store";
import useUserInfo from "./use-user-info";
import { QueryKeys } from "@/constants";

export const useVideoList = (): UseQueryResult<Video[], Error> => {
  const userInfo = useUserInfo();
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );
  const videoListRequest: VideoListRequest = {
    groupId: userInfo?.groupId,
    divisionId: userInfo?.divisionId,
    planId: activeCoverage?.planId,
    claimCoverageCode: activeCoverage?.claimCoverageCode,
    memberId: userInfo?.memberId,
    token: userInfo?.accessToken,
  };

  return useQuery({
    // Coverage is part of the request, so it must be part of the key too —
    // otherwise switching plans would keep showing the previous plan's videos
    queryKey: [
      QueryKeys.VIDEO_LIST,
      videoListRequest.memberId,
      videoListRequest.claimCoverageCode,
      videoListRequest.planId,
    ],
    queryFn: async () => {
      const videoListResponse = await fetchVideoList(videoListRequest);
      return videoListResponse?.data ?? [];
    },
    staleTime: Infinity,
    retry: false,
    retryOnMount: false,
    enabled: Boolean(videoListRequest.token),
  });
};
